import styled, { keyframes } from 'styled-components';
import { Button } from 'react-bootstrap';
import { theme } from '../../styles/theme';

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

export const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${theme.colors.background};
  background-image: ${theme.gradients.mesh};
  color: ${theme.colors.text};
  font-family: ${theme.fonts.secondary};
  padding: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  overflow-x: hidden;
  overflow-y: visible;

  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
  }
`;

export const Card = styled.div`
  position: relative;
  border-radius: ${theme.borderRadius.xlarge};
  padding: ${theme.spacing.xl};
  width: 100%;
  max-width: 1400px;
  background: ${theme.colors.backgroundLight};
  backdrop-filter: blur(20px);
  box-shadow: ${theme.shadows.card};
  overflow: visible;

  /* Gradient border effect */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: ${theme.borderRadius.xlarge};
    background: ${theme.gradients.cardBorder};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 208, 192, 0.3);
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.large};
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 10px;
  padding: ${theme.spacing.lg};
  background: ${theme.colors.glass};
  border: 1px solid ${theme.colors.glassBorder};
  border-radius: ${theme.borderRadius.large};
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(7, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    padding: ${theme.spacing.md};
  }
`;

export const NumberButton = styled(Button)<{ selected?: boolean }>`
  aspect-ratio: 1;
  display: flex;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: ${theme.fonts.secondary};
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  ${props => props.selected ? `
    background: ${theme.gradients.primary};
    color: ${theme.colors.backgroundDark};
    box-shadow: ${theme.shadows.neon};
    transform: scale(1.05);
    border-color: transparent;

    &::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      background: ${theme.gradients.primary};
      z-index: -1;
      filter: blur(8px);
      opacity: 0.5;
    }
  ` : `
    background: ${theme.colors.glass};
    color: ${theme.colors.text};
    border-color: ${theme.colors.glassBorder};

    &:hover:not(:disabled) {
      background: ${theme.colors.glassHover};
      border-color: rgba(0, 208, 192, 0.3);
      transform: scale(1.08);
      box-shadow: 0 0 20px rgba(0, 208, 192, 0.2);
    }
  `}

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const ActionButton = styled(Button)`
  background: ${theme.colors.backgroundLight};
  color: ${theme.colors.text};
  font-weight: bold;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  width: 100%;
  max-width: 300px;
  margin: ${theme.spacing.md} auto;
  display: block;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const StatusBadge = styled.span<{ $status: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 220px;
  padding: 0.875rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  border-radius: 999px;
  margin: 1.25rem auto;

  ${({ $status }) => {
    switch ($status) {
      case 'IDLE':
        return `
          background: rgba(107, 114, 128, 0.1);
          color: #9ca3af;
          border: 1px solid rgba(107, 114, 128, 0.2);
        `;
      case 'active':
        return `
          background: rgba(18, 78, 40, 0.95);
          color: #4ade80;
          border: 1px solid rgba(74, 222, 128, 0.35);
          box-shadow: 0 0 26px rgba(34, 197, 94, 0.18);
        `;
      case 'drawing':
        return `
          background: rgba(251, 191, 36, 0.1);
          color: #fbbf24;
          border: 1px solid rgba(251, 191, 36, 0.2);
          box-shadow: 0 0 20px rgba(251, 191, 36, 0.15);
        `;
      case 'completed':
        return `
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
          border: 1px solid rgba(99, 102, 241, 0.2);
        `;
      case 'cancelled':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.2);
        `;
      default:
        return `
          background: rgba(107, 114, 128, 0.1);
          color: #9ca3af;
          border: 1px solid rgba(107, 114, 128, 0.2);
        `;
    }
  }}
`;

export const ErrorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.small};
  font-size: 0.9rem;
  font-family: ${theme.fonts.secondary};
  background: rgba(255, 68, 68, 0.1);
  color: ${theme.colors.error};
  border: 1px solid ${theme.colors.error};
`;

export const DataDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${theme.colors.glassBorder};
  font-family: ${theme.fonts.secondary};

  &:last-child {
    border-bottom: none;
  }

  strong {
    font-weight: 500;
    color: ${theme.colors.textSecondary};
    font-size: 0.875rem;
  }

  span, & > *:last-child:not(strong) {
    font-weight: 600;
    color: ${theme.colors.text};
    font-size: 1rem;
  }
`;

export const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  background: rgba(255, 68, 68, 0.6);
  border: 1px solid ${theme.colors.error};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.small};
  margin: ${theme.spacing.md} 84px;
  text-align: center;
`;

export const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  background: rgba(68, 255, 68, 0.1);
  border: 1px solid ${theme.colors.success};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.small};
  margin: ${theme.spacing.md} 84px;
  text-align: center;
`;

export const LotteryInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  width: 100%;
  margin-top: ${theme.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const LotteryColumn = styled.div`
  position: relative;
  background: ${theme.colors.glass};
  border: 1px solid ${theme.colors.glassBorder};
  border-radius: ${theme.borderRadius.large};
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  /* Subtle gradient border on hover */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: ${theme.borderRadius.large};
    background: transparent;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    transition: background 0.3s ease;
  }

  &:hover {
    background: ${theme.colors.glassHover};
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.cardHover};

    &::before {
      background: ${theme.gradients.cardBorder};
    }
  }
`;

export const ColumnTitle = styled.h3`
  margin: 0 0 1.25rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, ${theme.colors.glassBorder}, transparent);
  }
`;


export const DrawingStateBanner = styled.div<{
  $variant?: 'drawing' | 'inactive' | 'active' | 'complete';
}>`
  width: 100%;
  padding: 10px 10px;
  background-color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'inactive':
        return theme.colors.error;
      case 'drawing':
        return theme.colors.primary;
      case 'active':
        return theme.colors.success;
      case 'complete':
        return theme.colors.green;
      default:
        return theme.colors.backgroundLight;
    }
  }};
  color: white;
  text-align: center;
  font-weight: bold;
  font-size: 1.1rem;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  transition: transform 0.3s ease;
  transform: translateY(${props => (props.$variant ? '0' : '-100%')});
  opacity: ${props => (props.$variant ? '1' : '0')};
  pointer-events: ${props => (props.$variant ? 'auto' : 'none')};
  border-radius: 15px 15px 15px 15px; /* Rounded bottom corners */
`;
